import ASBVApi from 'api/query/asbv';
import { ASBV } from 'api/query/asbv/asbv';
import { ASBVCategory } from 'api/query/asbv/asbv-category.enum';
import { AppNumConstants } from 'constants/app-num-constants.enum';
import { ASBVsFilterValue } from 'pages/search/asbvs/asbvs-filter-value';
import { Database } from 'shared/database.enum';
import { SubIndex } from 'shared/sub-index.enum';
import { StorageKey, addToStorage, getFromStorage } from './storage-utils';

/**
 * Gets the LAST `ASBV` from the selected ASBVs where the category is `Default`.
 */
export const getLastDefaultASBV = (filters: Map<string, ASBVsFilterValue>, asbvList: ASBV[]) => {
  // Filters the list for only default ASBVs.
  const filtered = asbvList.filter((a) => a.category === ASBVCategory.Default);

  let lastDefault = filtered[filtered.length - 1];
  // Loops through the list to find the last default ASBV where
  // it is also NOT an ASBV from the filters.
  for (let i = 0; i < filtered.length; i += 1) {
    const res = filters.get(filtered[i].asbvId);
    if (!res) {
      lastDefault = filtered[i];
    }
  }
  return lastDefault;
};

/**
 * Returns a new list of ASBVs based on the default ASBVs with
 * any ASBV selected in the filters included.
 *
 * The source ASBV list is either the cached ASBVs (if they exist), otherwise
 * it will resort to using the Defaults which is defined by the category.
 *
 * - If an ASBV is included in a filter and is also a default, do nothing.
 * - If an ASBV is included in a filter, is not a default and
 * the ASBVs is less than the max (12), push it to the end.
 * - If an ASBV is included in the filter, is not a default and
 * the ASBVs is greater than the max (12), replace the last default
 * ASBV where the default is NOT an ASBV selected in the filters.
 *
 * @param asbvs The entire list of ASBVs.
 * @param filters The selected filter values.
 * @param defaults The baseline ASBVs, uses these as a starting point to populate the returned list.
 * @param maxASBVs The maximum allowed ASBVs.
 */
export const getASBVsFromFilters = (asbvs: ASBV[], filters: Map<string, ASBVsFilterValue>, defaults: ASBV[], maxASBVs: number) => {
  const baseASBVs = [...defaults];
  filters.forEach((val, key) => {
    // Find if the filter ASBV exists in the defaults
    const found = defaults.find((d) => d.asbvId === val.asbv);

    if (!found) {
      const filterASBV = asbvs.find((a) => a.asbvId === key);
      if (baseASBVs.length < maxASBVs) {
        // If less than the max columns, get it from the ASBV list and push it
        if (filterASBV) {
          baseASBVs.push(filterASBV);
        }
      } else if (filterASBV) {
        // If more than the max columns, replace the last default where
        // the last default does not exist in the filters.
        const lastDefault = getLastDefaultASBV(filters, baseASBVs);
        baseASBVs.splice(baseASBVs.indexOf(lastDefault), 1);
        baseASBVs.push(filterASBV);
      }
    }
  });
  return baseASBVs;
};

/**
 * Gets the key used in local storage for the database and subindex.
 * @param db
 * @param subIndex
 */
export const getASBVStorageKey = (db: Database, subIndex?: SubIndex) => {
  let key = `${StorageKey.CustomASBVs}-${db}`;
  if (subIndex) {
    key += `-${subIndex}`;
  }
  return key;
};

/**
 * Gets the custom ASBVs from local storage for the Database and SubIndex.
 * @param db
 * @param subIndex
 */
export const getCustomASBVsFromStorage = (db: Database, subIndex?: SubIndex): ASBV[] => {
  const key = getASBVStorageKey(db, subIndex);
  return getFromStorage<ASBV[]>(key);
};

/**
 * Saves the custom ASBVs to local storage for the Database and SubIndex.
 * @param asbvs
 * @param db
 * @param subIndex
 */
export const saveCustomASBVsToStorage = (asbvs: ASBV[], db: Database, subIndex: SubIndex) => {
  const key = getASBVStorageKey(db, subIndex);

  addToStorage(key, asbvs);
};

/**
 * Gets the initial state ASBV list.
 *
 * This first retrieves the cached ASBVs from local storage then
 * sets the initial ASBVs based on selected filters.
 */
export const getASBVs = async (db: Database, subIndex?: SubIndex, filters?: Map<string, ASBVsFilterValue>) => {
  const cacheASBVs = getCustomASBVsFromStorage(db, subIndex);
  const asbvs: ASBV[] = cacheASBVs;

  if (asbvs?.length) {
    if (!filters || filters.size === 0) {
      return asbvs;
    }
    const asbvsGroup = await ASBVApi.getGroup(db, subIndex);

    const filterASBVs = getASBVsFromFilters(asbvsGroup, filters, asbvs, AppNumConstants.MaxASBVs);
    return filterASBVs;
  }
  return [];
};
